/**
 * W7 / H08 / R9 — ranking disclosure BottomSheet („Jak řadíme výsledky“).
 * Mode-aware Relevance / Distance / Promo shell (F04-UI).
 *
 * H09 / F03 LEGAL marketplace–seller classification remains **Blocked** — this sheet
 * must not invent classification UI or copy. Do not false-green H09.
 * Promo mode is a placeholder shell for paid-ranking disclosure only (locale BodyPromo).
 */

import { useEffect, useId } from 'react';
import { useTranslation } from 'react-i18next';
import { useOptionalOverlayBackDismiss } from '../../shared/context/OverlayBackDismissContext.js';
import { BottomSheet } from '../../shared/ui/BottomSheet.js';
import { Button } from '../../shared/ui/Button.js';
import type { DiscoverySort } from '../discovery/discoverySearchParams.js';

/** Live URL sorts + promo shell mode (paid ranking disclosure; not a DiscoverySort yet). */
export type BrowseRankingDisclosureMode = DiscoverySort | 'promo';

export type BrowseRankingDisclosureSheetProps = {
  readonly open: boolean;
  readonly onClose: () => void;
  /** Active disclosure mode — drives which body is primary. */
  readonly sortMode: BrowseRankingDisclosureMode;
};

export type RankingDisclosureBodyKey =
  | 'browse.discovery.rankingDisclosureBodyRelevance'
  | 'browse.discovery.rankingDisclosureBodyDistance'
  | 'browse.discovery.rankingDisclosureBodyPromo';

/**
 * Ordered body keys: [primary, secondary, tertiary].
 * Promo-primary is a placeholder shell (H08) — BAN marketplace LEGAL classification copy.
 */
export function resolveRankingDisclosureBodyKeys(
  sortMode: BrowseRankingDisclosureMode,
): readonly [
  RankingDisclosureBodyKey,
  RankingDisclosureBodyKey,
  RankingDisclosureBodyKey,
] {
  if (sortMode === 'distance') {
    return [
      'browse.discovery.rankingDisclosureBodyDistance',
      'browse.discovery.rankingDisclosureBodyRelevance',
      'browse.discovery.rankingDisclosureBodyPromo',
    ];
  }
  if (sortMode === 'promo') {
    return [
      'browse.discovery.rankingDisclosureBodyPromo',
      'browse.discovery.rankingDisclosureBodyRelevance',
      'browse.discovery.rankingDisclosureBodyDistance',
    ];
  }
  return [
    'browse.discovery.rankingDisclosureBodyRelevance',
    'browse.discovery.rankingDisclosureBodyDistance',
    'browse.discovery.rankingDisclosureBodyPromo',
  ];
}

const SLOT_TEST_IDS = [
  'customer-browse-ranking-disclosure-primary',
  'customer-browse-ranking-disclosure-secondary',
  'customer-browse-ranking-disclosure-promo',
] as const;

export function BrowseRankingDisclosureSheet(
  props: BrowseRankingDisclosureSheetProps,
): JSX.Element {
  const { open, onClose, sortMode } = props;
  const { t } = useTranslation('customer');
  const titleId = useId();
  const descriptionId = useId();
  const overlayBack = useOptionalOverlayBackDismiss();
  const registerOverlayDismiss = overlayBack?.registerOverlayDismiss;

  useEffect(() => {
    if (!open || registerOverlayDismiss === undefined) {
      return;
    }
    return registerOverlayDismiss(onClose);
  }, [open, onClose, registerOverlayDismiss]);

  const title = t('browse.discovery.rankingDisclosureTitle');
  const bodyKeys = resolveRankingDisclosureBodyKeys(sortMode);

  return (
    <BottomSheet
      open={open}
      onClose={onClose}
      title={title}
      titleId={titleId}
      describedBy={descriptionId}
      closeLabel={t('browse.discovery.rankingDisclosureClose')}
      testId="customer-browse-ranking-disclosure-sheet"
      className="max-h-[90vh] bg-[var(--color-surface-elevated)] p-5 lg:max-w-lg"
      containerClassName="lg:items-center"
    >
      <div
        className="flex flex-col gap-4"
        data-testid="customer-browse-ranking-disclosure-body"
        data-sort-mode={sortMode}
      >
        {bodyKeys.map((bodyKey, index) => (
          <p
            key={`${sortMode}-${bodyKey}`}
            id={index === 0 ? descriptionId : undefined}
            className={[
              'text-sm',
              index === 0
                ? 'text-[var(--color-on-surface)]'
                : 'text-[var(--color-on-surface-muted)]',
            ].join(' ')}
            data-testid={SLOT_TEST_IDS[index]}
            data-body-key={bodyKey}
          >
            {t(bodyKey)}
          </p>
        ))}
        <Button
          type="button"
          intent="secondary"
          size="md"
          block
          data-testid="customer-browse-ranking-disclosure-close"
          onClick={onClose}
        >
          {t('browse.discovery.rankingDisclosureClose')}
        </Button>
      </div>
    </BottomSheet>
  );
}
